import React, {useMemo} from 'react'
import queryString from 'query-string'
import {useDispatch, useSelector} from 'react-redux'
import {useQuery} from '@apollo/client'
import {EMPLOYEE_DEPENDENTS, CHILDCARE_PROCEDURE_STATUS_ON_ADD_DEPENDENTS} from './query'
import {useHistory, useLocation} from 'react-router-dom'
import api from 'api'
import Form, {autofill} from 'procedures/changeDependents/add/Form'
import {actionCreators} from 'actions'
import {dependentDefaultValue} from 'employees/form/common'
import {handlerNestFormResponse} from 'libs/errorHandler'
import {asyncError} from 'store/actions/asyncError'
import {useFetchEffect} from 'hooks/useFetchEffect'

const toString = (value) => {
  return value === null || value === undefined ? '' : String(value)
}

const toFile = (file) => {
  return file.filename === null && file.url === null ? null : file
}

const LoadFromExistDependents = ({defaultDependents, employeeId, dependentIds, renderForm}) => {
  const {loading, data} = useQuery(EMPLOYEE_DEPENDENTS, {
    variables: {employeeId: employeeId, dependentIds},
    fetchPolicy: 'network-only',
  })

  const dependents = useMemo(() => {
    if (loading || !data) {
      return defaultDependents
    } else {
      return data.client.employee.employeeDependentsWhereByIds.map((dependent) => ({
        ...dependentDefaultValue(),
        id: dependent.id,
        first_name: dependent.firstName,
        last_name: dependent.lastName,
        first_name_kana: dependent.firstNameKana,
        last_name_kana: dependent.lastNameKana,
        birthday: dependent.birthday,
        gender: dependent.gender,
        relation_other: dependent.relationName,
        relation_type: dependent.relationType,
        relation_report_type: dependent.relationReportType,
        residence_status: dependent.residenceStatus,
        domestic_remittance: toString(dependent.domesticRemittance),
        remittance: toString(dependent.remittance),
        prefecture_id: dependent.prefecture?.id,
        postcode_0: dependent.postcode0,
        postcode_1: dependent.postcode1,
        city: dependent.city,
        house_number: dependent.houseNumber,
        building: dependent.building,
        address_kana: dependent.addressKana,
        address_foreign: dependent.addressForeign,
        job: dependent.job,
        phone_number_0: dependent.phoneNumber0,
        phone_number_1: dependent.phoneNumber1,
        phone_number_2: dependent.phoneNumber2,
        basic_pension_number_0: dependent.basicPensionNumber0,
        basic_pension_number_1: dependent.basicPensionNumber1,
        national_type: dependent.nationalType,
        romaji_name: dependent.romajiName,
        romaji_name_kana: dependent.romajiNameKana,
        annual_income: toString(dependent.annualIncome),
        annual_earnings: toString(dependent.annualEarnings),
        handicap_type: dependent.handicapType,
        handicap_detail: dependent.handicapDetail,
        handicap_certificate: toFile(dependent.handicapCertificate),
        is_non_resident: dependent.isNonResident,
        is_study_abroad: dependent.isStudyAbroad,
        related_to_relatives_document: toFile(dependent.relatedToRelativesDocument),
        related_to_remittance_document: toFile(dependent.relatedToRemittanceDocument),
        proving_study_abroad_document: toFile(dependent.provingStudyAbroadDocument),
        dependent_social_insurance: dependent.dependentSocialInsurance,
        dependent_from: dependent.dependentFrom,
        dependent_reason: dependent.dependentReason,
        dependent_tax_law: dependent.dependentTaxLaw,
        dependent_tax_law_from: dependent.dependentTaxLawFrom,
        dependent_tax_law_reason: dependent.dependentTaxLawReason,
      }))
    }
  }, [defaultDependents, loading, data])

  return renderForm({dependents})
}

const LoadFromMaternityProcedureStatus = ({defaultDependents, maternityProcedureStatusId, renderForm}) => {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.auth.token)
  const procedureStatus = useSelector((state) => state.procedureStatuses.current.data)

  useFetchEffect(() => {
    dispatch(
      actionCreators.procedureStatuses.current.fetchData(
        api.createWithAuth(token).procedureStatuses.get(maternityProcedureStatusId, ['procedure_maternity_datum'])
      )
    )
    return () => {
      dispatch(actionCreators.procedureStatuses.current.destroy())
    }
  }, [maternityProcedureStatusId])

  const props = useMemo(() => {
    const procedureMaternityDatum =
      procedureStatus.procedure_finish_maternity_datum ||
      procedureStatus.procedure_extend_maternity_datum ||
      procedureStatus.procedure_start_maternity_datum

    const children = procedureMaternityDatum?.procedure_maternity_child_data?.filter((c) => c.add_support) || []
    if (children) {
      // 対象の子が1人以上いて、全員出産年月日が同じ場合のみ被扶養者異動日を設定
      const dateOn = (() => {
        const [birthDate, notUnique] = new Set(children.map((c) => c.birth_date))
        return birthDate && !notUnique ? birthDate : null
      })()
      const dependents = children.map((child) => ({
        ...dependentDefaultValue(),
        first_name: child.first_name,
        first_name_kana: child.first_name_kana,
        last_name: child.last_name,
        last_name_kana: child.last_name_kana,
        birthday: child.birth_date,
        dependent_reason: '出生',
        relation_type: 'other',
        job: '乳児',
        annual_income: '0',
        annual_earnings: '0',
      }))
      return {dateOn, dependents}
    } else {
      return {dateOn: null, dependents: defaultDependents}
    }
  }, [defaultDependents, procedureStatus])

  return renderForm(props)
}

const LoadFromChildcareProcedureStatus = ({defaultDependents, childcareProcedureStatusId, renderForm}) => {
  const {loading, data} = useQuery(CHILDCARE_PROCEDURE_STATUS_ON_ADD_DEPENDENTS, {
    variables: {procedureStatusId: childcareProcedureStatusId},
    fetchPolicy: 'network-only',
  })
  const child = data?.client?.procedureStatus?.procedureChildcareDatum?.procedureChildcareChildDatum

  const props = useMemo(() => {
    if (loading || !child) {
      return {dateOn: null, dependents: defaultDependents}
    } else {
      const dependents = [
        {
          ...dependentDefaultValue(),
          first_name: child.firstName,
          first_name_kana: child.firstNameKana,
          last_name: child.lastName,
          last_name_kana: child.lastNameKana,
          birthday: child.birthDate,
          dependent_reason: '出生',
          relation_type: 'other',
          job: '乳児',
          annual_income: '0',
          annual_earnings: '0',
        },
      ]
      return {dateOn: child.birthDate, dependents}
    }
  }, [defaultDependents, loading, child])

  return renderForm(props)
}

export const Detail = () => {
  const history = useHistory()
  const {search} = useLocation()
  const {
    employee_id: employeeId,
    maternity_procedure_status_id: maternityProcedureStatusId,
    childcare_procedure_status_id: childcareProcedureStatusId,
    dependentIds,
  } = queryString.parse(search, {arrayFormat: 'bracket'})

  const token = useSelector((state) => state.auth.token)
  const employee = useSelector((state) => state.employees.current.data)
  const defaultDependents = useMemo(() => [dependentDefaultValue()], [])
  const dispatch = useDispatch()

  const handleSubmit = async ({
    dependents,
    marital_status,
    spouse_annual_income,
    spouse_monthly_income,
    date_on: dateOn,
    procedure_status_id: procedureStatusId,
  }) => {
    const authedApi = api.createWithAuth(token)
    try {
      if (!procedureStatusId) {
        const {
          data: {id},
        } = await authedApi.procedureStatuses.start(employeeId, 'add_dependents', dateOn, 'draft', false)
        procedureStatusId = id
        dispatch(autofill('procedure_status_id', procedureStatusId))
      } else {
        await authedApi.procedureStatuses.update(procedureStatusId, {date_on: dateOn})
      }

      await authedApi.procedureStatuses.addDependentsData
        .updateAll(procedureStatusId, dependents)
        .catch(handlerNestFormResponse('dependents'))
      await authedApi.employees.update(employeeId, {marital_status, spouse_annual_income, spouse_monthly_income})
      await authedApi.procedureStatuses.update(procedureStatusId, {initialized: true})
      history.push(`/add_dependents/flow/${procedureStatusId}`)
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  useFetchEffect(() => {
    dispatch(
      actionCreators.employees.current.fetchData(api.createWithAuth(token).employees.get(employeeId, ['office']))
    )
    return () => {
      dispatch(actionCreators.employees.current.destroy())
    }
  }, [employeeId])

  const renderForm = ({dependents, dateOn}) => (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        dependents: dependents,
        marital_status: employee.marital_status,
        spouse_annual_income: employee.spouse_annual_income,
        spouse_monthly_income: employee.spouse_monthly_income,
        date_on: dateOn || null,
      }}
      employee={employee}
    />
  )

  if (dependentIds) {
    return (
      <LoadFromExistDependents
        defaultDependents={defaultDependents}
        employeeId={employeeId}
        dependentIds={dependentIds}
        renderForm={renderForm}
      />
    )
  } else if (maternityProcedureStatusId) {
    return (
      <LoadFromMaternityProcedureStatus
        defaultDependents={defaultDependents}
        maternityProcedureStatusId={maternityProcedureStatusId}
        renderForm={renderForm}
      />
    )
  } else if (childcareProcedureStatusId) {
    return (
      <LoadFromChildcareProcedureStatus
        defaultDependents={defaultDependents}
        childcareProcedureStatusId={childcareProcedureStatusId}
        renderForm={renderForm}
      />
    )
  } else {
    return renderForm({dependents: defaultDependents, dateOn: null})
  }
}

export default Detail
